import React, { useState, useEffect } from 'react';
import './ShareButtons.css';

const ShareButtons = ({ cardName, price, currencySymbol, username, card }) => {
  const [showCopiedTooltip, setShowCopiedTooltip] = useState(false);

  // Update Open Graph meta tags for social media sharing
  useEffect(() => {
    const updateMetaTags = () => {
      const cardImage = card.image || '';
      const description = `You can sell your ${cardName} for ${currencySymbol}${price.toFixed(2)}`;

      // Update or create Open Graph meta tags
      const updateMetaTag = (property, content) => {
        let meta = document.querySelector(`meta[property="${property}"]`);
        if (!meta) {
          meta = document.createElement('meta');
          meta.setAttribute('property', property);
          document.head.appendChild(meta);
        }
        meta.setAttribute('content', content);
      };

      // Update or create Twitter Card meta tags
      const updateTwitterTag = (name, content) => {
        let meta = document.querySelector(`meta[name="${name}"]`);
        if (!meta) {
          meta = document.createElement('meta');
          meta.setAttribute('name', name);
          document.head.appendChild(meta);
        }
        meta.setAttribute('content', content);
      };

      if (cardImage) {
        // Open Graph tags (Facebook, LinkedIn, etc.)
        updateMetaTag('og:title', cardName);
        updateMetaTag('og:description', description);
        updateMetaTag('og:image', cardImage);
        updateMetaTag('og:url', window.location.href);
        updateMetaTag('og:type', 'website');

        // Twitter Card tags
        updateTwitterTag('twitter:card', 'summary_large_image');
        updateTwitterTag('twitter:title', cardName);
        updateTwitterTag('twitter:description', description);
        updateTwitterTag('twitter:image', cardImage);
      }
    };

    updateMetaTags();
  }, [cardName, price, currencySymbol, card.image]);

  // Generate share URL with search parameters
  const generateShareUrl = () => {
    const baseUrl = window.location.origin;
    const params = new URLSearchParams({
      search: cardName,
      game: card.gameName || ''
    });

    // Add expansion/set if available
    if (card.expansionName || card.setName || card.set_name) {
      params.set('set', card.expansionName || card.setName || card.set_name);
    }

    return `${baseUrl}/${username}?${params.toString()}`;
  };

  // Generate share message
  const generateShareMessage = () => {
    return `You can sell your ${cardName} for ${currencySymbol}${price.toFixed(2)} at`;
  };

  const shareUrl = generateShareUrl();
  const shareMessage = generateShareMessage();

  const shareToFacebook = () => {
    // Facebook will use Open Graph meta tags when available, but we can include the share URL
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareMessage + ' ' + shareUrl)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const shareToX = () => {
    // X/Twitter will use meta tags when available. Include image reference in text
    const cardImage = card.image || '';
    const text = `${shareMessage} ${shareUrl}`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const shareToReddit = () => {
    // Reddit doesn't support image parameter directly, but will preview the image from the page
    const url = `https://reddit.com/submit?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareMessage)}`;
    window.open(url, '_blank', 'width=800,height=600');
  };

  const copyForInstagram = () => {
    // For Instagram, copy the message and URL. User will need to manually add the image
    const text = `${shareMessage} ${shareUrl}`;
    navigator.clipboard.writeText(text).then(() => {
      setShowCopiedTooltip(true);
      setTimeout(() => setShowCopiedTooltip(false), 3000);
    }).catch(err => {
      console.error('Failed to copy:', err);
      alert('Failed to copy to clipboard');
    });
  };

  return (
    <div className="share-buttons">
      <button
        className="share-btn share-btn-facebook"
        onClick={shareToFacebook}
        title="Share on Facebook"
        aria-label="Share on Facebook"
      >
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      </button>

      <button
        className="share-btn share-btn-x"
        onClick={shareToX}
        title="Share on X"
        aria-label="Share on X"
      >
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      </button>

      <button
        className="share-btn share-btn-reddit"
        onClick={shareToReddit}
        title="Share on Reddit"
        aria-label="Share on Reddit"
      >
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
        </svg>
      </button>

      <button
        className="share-btn share-btn-instagram"
        onClick={copyForInstagram}
        title="Copy for Instagram"
        aria-label="Copy for Instagram"
      >
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
        </svg>
        {showCopiedTooltip && <span className="copied-tooltip">Copied! Paste in Instagram</span>}
      </button>
    </div>
  );
};

export default ShareButtons;
